import { useState } from 'react';

/**
 * Campo numérico que se puede dejar vacío.
 *
 * Nace de un problema real encontrado cargando trabajos desde el teléfono: el
 * campo de cantidad guardaba un número en el estado, así que al borrarlo
 * `Number('')` daba 0 y el campo se rellenaba solo con un cero. Para cargar 300
 * había que acordarse de borrar ese cero primero; si no, quedaba `0300`.
 *
 * El arreglo no es filtrar ceros a la izquierda —eso sería tapar el síntoma—
 * sino admitir que **un campo numérico tiene un estado que no es un número**:
 * el de estar vacío mientras alguien lo está escribiendo. Por eso el borrador
 * se guarda como texto y sólo se emite un número cuando hay uno.
 *
 * Entre `''` y `0` hay una diferencia que importa: "no escribí nada todavía" no
 * es lo mismo que "vale cero". Cero kilómetros cobrados es un dato; un campo
 * vacío es una pregunta sin responder.
 */
export function CampoNumero({
  value,
  onChange,
  entero = false,
  min,
  max,
  placeholder,
  className = '',
  'data-testid': testId,
  ...resto
}: {
  value: number | null;
  onChange: (n: number | null) => void;
  /** Cantidades de animales son enteras; kilos y porcentajes no. */
  entero?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'min' | 'max'
>) {
  const [borrador, setBorrador] = useState(value === null ? '' : String(value));

  // El valor puede cambiar desde AFUERA —al elegir otro honorario, al abrir un
  // trabajo para editar—, y entonces el borrador tiene que seguirlo. Pero no
  // debe seguirlo cuando el cambio vino del propio tecleo: si alguien escribió
  // `3.` el borrador vale `3.` y el número vale 3, y reescribirlo le borraría
  // el punto en mitad de la palabra.
  //
  // Se distingue recordando cuál fue el último valor que se vio, y ajustando el
  // estado durante el render en vez de en un efecto. Es el patrón que React
  // recomienda para derivar estado de props: un efecto acá provocaría un render
  // de más y, en React 19, lo marca el linter.
  const [ultimoVisto, setUltimoVisto] = useState(value);
  if (value !== ultimoVisto) {
    setUltimoVisto(value);
    setBorrador(value === null ? '' : String(value));
  }

  return (
    <input
      {...resto}
      type="number"
      inputMode={entero ? 'numeric' : 'decimal'}
      min={min}
      max={max}
      placeholder={placeholder}
      value={borrador}
      onChange={(e) => {
        const texto = e.target.value;
        setBorrador(texto);
        if (texto === '') {
          onChange(null);
          return;
        }
        const n = Number(texto);
        if (!Number.isNaN(n)) onChange(n);
      }}
      onBlur={(e) => {
        // Al salir del campo se normaliza lo que quedó a medio escribir: `3.`
        // pasa a `3`, `007` pasa a `7`. Se hace acá y no mientras se teclea
        // para no pelearle a la persona el cursor.
        const n = borrador === '' ? null : Number(borrador);
        setBorrador(n === null || Number.isNaN(n) ? '' : String(n));
        resto.onBlur?.(e);
      }}
      className={className}
      data-testid={testId}
    />
  );
}
